import { Fragment, useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import logger from '@/utils/logger';
import LocalizedLink from '@/components/LocalizedLink';
import { PREVIEW_LIMIT } from '../utils/constants';

// 常量定义
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const PASTE_GUARD_MS = 1000; // 1秒防重复
const SUPPORTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const EXTENSION_TO_MIME = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
};
const ERROR_TYPES = {
  FILE_TYPE: 'file-type',
  FILE_SIZE: 'file-size',
  PROCESSING: 'processing'
};

function normalizeImageType(file) {
  if (!file) return '';
  const rawType = String(file.type || '').toLowerCase();
  if (rawType) return rawType;
  const name = String(file.name || '');
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  return EXTENSION_TO_MIME[ext] || '';
}

function ToolSection({
  onImageUpload,
  headingLevel = 'h1',
  enablePaste = true,
  titleText,
  subtitleText,
  subtitleText2,
  actionTo,
  actionLabel,
  actionHint,
  chooseFileLabel,
  chooseButtonClassName = '',
  chooseButtonStyle = {},
  supportsText,
  instructionText,
  instructionElement = 'h2',
  showHeader = true,
  showUploadIcon = true,
  showInlineChooseText = true,
  sectionClassName = 'bg-gray-50 py-6',
  containerClassName = 'container mx-auto px-4 text-center',
  uploadZoneClassName = '',
  uploadZoneStyle = {},
  exampleImages = [],
  exampleLabel,
  exampleHint,
  compact = false,
  variant = 'default',
}) {
  const { t } = useTranslation()
  const fileInputRef = useRef(null);
  const uploadLiveRef = useRef(null); // 替代getElementById
  const [error, setError] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);
  const lastUrlRef = useRef(null);
  const pasteGuardRef = useRef(0);
  const log = useCallback((event, payload = {}) => {
    logger.debug(`[ToolSection] ${event}`, payload);
  }, []);

  const resolvedTitle = titleText || t('tool.title')
  const resolvedSubtitle = subtitleText || t('tool.subtitle')
  const resolvedSubtitle2 = subtitleText2 || t('tool.subtitle2')
  const resolvedChooseFileLabel = chooseFileLabel || t('tool.chooseFile')
  const resolvedSupportsText = supportsText || t('tool.supports')
  const resolvedInstructionText = instructionText || t('tool.dragOrClick')
  const hasExamples = exampleImages.length > 0
  const InstructionTag = instructionElement === 'p' ? 'p' : 'h2'

  const revokeLastObjectUrl = useCallback(() => {
    if (!lastUrlRef.current || typeof lastUrlRef.current !== 'string' || !lastUrlRef.current.startsWith('blob:')) {
      lastUrlRef.current = null
      return
    }
    try {
      URL.revokeObjectURL(lastUrlRef.current)
    } catch (e) {
      if (import.meta.env.DEV) {
        log('revokeObjectURL:failed', { error: e?.message })
      }
    }
    lastUrlRef.current = null
  }, [log])

  const handleFileSelect = useCallback(async (file) => {
    if (isPreparing) return;
    setError(''); // Reset stale error when starting a new interaction
    log('handleFileSelect:start', {
      hasFile: !!file,
      name: file?.name,
      type: file?.type,
      size: file?.size,
    });
    if (!file) return;
    const normalizedType = normalizeImageType(file);
    const isImageLike = file.type ? file.type.startsWith('image/') : Boolean(normalizedType);
    if (!isImageLike) {
      setError(t('errors.onlyPngJpg'));
      log('handleFileSelect:reject-non-image', { 
        name: file.name, 
        type: file.type,
        errorType: ERROR_TYPES.FILE_TYPE 
      });
      return;
    }
    if (normalizedType && !SUPPORTED_IMAGE_TYPES.has(normalizedType)) {
      setError(t('errors.onlyPngJpg'));
      log('handleFileSelect:reject-unsupported', {
        name: file.name,
        type: file.type,
        normalizedType,
        errorType: ERROR_TYPES.FILE_TYPE
      });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError(t('errors.fileTooLarge10'));
      log('handleFileSelect:reject-too-large', { 
        name: file.name, 
        size: file.size,
        errorType: ERROR_TYPES.FILE_SIZE 
      });
      return;
    }
    setIsPreparing(true);
    // Downscale oversized images in the browser to keep previews responsive
    const MAX_PREVIEW_DIM = PREVIEW_LIMIT.maxEdge;
    try {
    const { downscaleFileToBlob, loadImageFromFile } = await import('../utils/resizeImage');
    const { img, url: tmpUrl } = await loadImageFromFile(file);
      const needsDownscale = Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height) > MAX_PREVIEW_DIM;
      URL.revokeObjectURL(tmpUrl);
      let finalUrl;
      if (needsDownscale) {
        const blob = await downscaleFileToBlob(file, MAX_PREVIEW_DIM, { preferPng: file.type === 'image/png', quality: 0.9, pixelated: false });
        finalUrl = URL.createObjectURL(blob);
          log('handleFileSelect:downscaled', { name: file.name, original: { w: img.naturalWidth, h: img.naturalHeight }, limit: MAX_PREVIEW_DIM });
      } else {
        finalUrl = URL.createObjectURL(file);
        log('handleFileSelect:use-original', { name: file.name });
      }
      revokeLastObjectUrl()
      lastUrlRef.current = finalUrl;
      log('onImageUpload:call', { finalUrl });
      onImageUpload(finalUrl);
      log('onImageUpload:success', { finalUrl });
      log('handleFileSelect:uploaded', { name: file.name, url: finalUrl });
      if (uploadLiveRef.current) {
        uploadLiveRef.current.textContent = t('upload.selected', { name: file.name });
      }
    } catch (e) {
      setError(t('errors.prepareFail'));
      if (import.meta.env.DEV) {
        log('handleFileSelect:error-details', { 
          name: file?.name, 
          error: e?.message,
          stack: e?.stack,
          errorType: ERROR_TYPES.PROCESSING
        });
      }
      log('handleFileSelect:error', { 
        name: file?.name, 
        message: e?.message,
        errorType: ERROR_TYPES.PROCESSING
      });
    } finally {
      setIsPreparing(false);
      log('handleFileSelect:complete', { name: file?.name });
    }
  }, [isPreparing, log, onImageUpload, revokeLastObjectUrl, t]);

  const handleExampleSelect = useCallback((example) => {
    if (isPreparing || !example?.src) return
    setError('')
    revokeLastObjectUrl()
    onImageUpload(example.src)
    log('example-select', { id: example.id, src: example.src })
    if (uploadLiveRef.current) {
      uploadLiveRef.current.textContent = t('upload.selectedExample', { name: example.label })
    }
  }, [isPreparing, log, onImageUpload, revokeLastObjectUrl, t])

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (isPreparing) return;
    setError('');
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      log('drop', { name: file?.name, size: file?.size });
      handleFileSelect(file);
      e.dataTransfer.clearData();
    }
  }, [handleFileSelect, isPreparing, log]);

  // Only enable global paste upload on the landing view to avoid double-binding the Editor listeners
  useEffect(() => {
    if (!enablePaste) return;
    const onPaste = (e) => {
      // Prevent duplicate paste handling within a short interval
      const now = Date.now();
      if (now - (pasteGuardRef.current || 0) < PASTE_GUARD_MS) return;
      const files = Array.from(e.clipboardData?.files || []);
      if (files.length) {
        e.preventDefault();
        pasteGuardRef.current = now;
        const file = files[0];
        log('paste', { name: file?.name, size: file?.size });
        handleFileSelect(file);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFileSelect, enablePaste, log]);

  const openFilePicker = useCallback(() => {
    if (isPreparing) return;
    setError('');
    fileInputRef.current?.click();
    log('click-open');
  }, [isPreparing, log]);

  const handleFileInputChange = useCallback((e) => {
    if (isPreparing) return;
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      log('input-change', { name: file?.name, size: file?.size });
      handleFileSelect(file);
      // 重置文件输入，允许重复选择同一文件
      e.target.value = '';
    }
  }, [handleFileSelect, isPreparing, log]);

  const handleZoneClick = useCallback((e) => {
    if (isPreparing) return;
    if (e.target instanceof Element && e.target.closest('button')) return;
    openFilePicker();
  }, [isPreparing, openFilePicker]);

  const handleChooseFileClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    openFilePicker();
  }, [openFilePicker]);

  return (
    <section id="tool" className={sectionClassName}>
      <div className={containerClassName}>
        {showHeader ? (
          <Fragment>
            {(() => {
              const HeadingTag = headingLevel === 'h2' ? 'h2' : 'h1';
              return (
                <HeadingTag
                  className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-gray-800 mb-3 text-center"
                  style={{ fontSize: 'clamp(1.5rem, 2.2vw + 1rem, 3rem)', lineHeight: 1.1, marginBottom: '0.75rem' }}
                >
                  {resolvedTitle}
                </HeadingTag>
              );
            })()}
            <div className="mb-6 max-w-4xl md:max-w-5xl mx-auto" style={{ marginBottom: '1.5rem' }}>
              <p className="text-sm sm:text-base text-gray-600 mb-2" style={{ lineHeight: 1.5, marginBottom: '0.5rem' }}>
                {resolvedSubtitle}
              </p>
              {resolvedSubtitle2 ? (
                <p className="text-sm sm:text-base text-gray-600" style={{ lineHeight: 1.5 }}>
                  {resolvedSubtitle2}
                </p>
              ) : null}
              {actionTo && actionLabel ? (
                <div className="mt-4 flex flex-col items-center gap-2">
                  <LocalizedLink
                    to={actionTo}
                    className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"
                  >
                    {actionLabel}
                  </LocalizedLink>
                  {actionHint ? (
                    <p className="text-xs sm:text-sm text-gray-500">
                      {actionHint}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </Fragment>
        ) : null}
        <div 
          className={`upload-zone relative max-w-3xl mx-auto bg-white border-2 border-dashed border-gray-300 rounded-xl transition-all hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isPreparing ? 'cursor-not-allowed opacity-80 ring-1 ring-blue-200' : 'cursor-pointer'} ${uploadZoneClassName}`}
          style={{ padding: compact ? '0.75rem 1.25rem' : '1.5rem', minHeight: compact ? 'auto' : '16rem', borderWidth: '2px', borderStyle: 'dashed', borderColor: '#d1d5db', borderRadius: '0.75rem', backgroundColor: '#fff', ...uploadZoneStyle }}
          aria-busy={isPreparing}
          role={variant === 'sprite' ? 'button' : undefined}
          tabIndex={variant === 'sprite' ? 0 : undefined}
          aria-label={variant === 'sprite' ? (resolvedInstructionText || resolvedChooseFileLabel) : undefined}
          onClick={handleZoneClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (e.target === e.currentTarget) {
                e.preventDefault();
                handleZoneClick(e);
              }
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          data-testid="upload-zone"
        >
          <input
            type="file"
            id="file-input"
            ref={fileInputRef}
            className="hidden"
            accept="image/png,image/jpeg,image/gif,image/webp"
            aria-label={t('tool.ariaChoose')}
            disabled={isPreparing}
            onChange={handleFileInputChange}
            data-testid="file-input"
          />
          {compact ? (
            <div className="flex items-center justify-between gap-3 text-left">
              <div className="flex items-center gap-3 min-w-0">
                <svg className="h-6 w-6 text-blue-600 shrink-0" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M20 16.5a4.5 4.5 0 0 0-1.7-8.67 6 6 0 0 0-11.6 1.64A4 4 0 0 0 6 16" />
                  <path d="M12 16V8" />
                  <path d="M9 11l3-3 3 3" />
                </svg>
                <div className="truncate">
                  <span id="upload-instructions" className="text-sm font-semibold text-gray-800">
                    {resolvedInstructionText}
                  </span>
                  <span id="upload-supports" className="hidden sm:inline text-xs text-gray-500 ml-2">
                    {resolvedSupportsText}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className={`btn-primary shrink-0 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-1.5 transition-colors ${chooseButtonClassName} ${isPreparing ? 'opacity-60 cursor-not-allowed' : ''}`}
                style={chooseButtonStyle}
                data-testid="choose-file-btn"
                aria-describedby="upload-instructions upload-supports"
                onClick={handleChooseFileClick}
                disabled={isPreparing}
              >
                {resolvedChooseFileLabel}
              </button>
            </div>
          ) : (
            <div className="text-center">
              {showUploadIcon ? (
                variant === 'sprite' ? (
                <svg className="mx-auto h-12 w-12 text-blue-500" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M20 16.5a4.5 4.5 0 0 0-1.7-8.67 6 6 0 0 0-11.6 1.64A4 4 0 0 0 6 16" />
                  <path d="M12 16V8" />
                  <path d="M9 11l3-3 3 3" />
                </svg>
                ) : (
                  <svg className="mx-auto h-12 w-12 text-gray-400" width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" focusable="false"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M7 16a4 4 0 01-4-4V7a4 4 0 014-4h10a4 4 0 014 4v5a4 4 0 01-4 4H7z" /></svg>
                )
              ) : null}
              <InstructionTag id="upload-instructions" className={`${showUploadIcon ? 'mt-4' : ''} text-xl font-semibold text-gray-700`} style={{ fontSize: '1.25rem', lineHeight: '1.75rem', marginTop: showUploadIcon ? '1rem' : 0, marginBottom: 0 }}>
                {resolvedInstructionText}
                {showInlineChooseText ? (
                  <Fragment>
                    {' '}
                    <span className="text-blue-600">{t('tool.clickToChoose')}</span>
                  </Fragment>
                ) : null}
              </InstructionTag>
              <p
                id="upload-supports"
                className="mt-1 text-sm text-gray-500"
                style={{ fontSize: '0.875rem', lineHeight: '1.25rem', marginTop: '0.25rem', marginBottom: 0 }}
              >
                {resolvedSupportsText}
              </p>
              {hasExamples ? (
                <div className="mt-4" style={{ marginTop: '1rem' }}>
                  <p className="text-sm font-medium text-gray-600">
                    {exampleLabel}
                  </p>
                  {exampleHint ? (
                    <p className="mt-1 text-sm text-gray-500">
                      {exampleHint}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap justify-center gap-2">
                    {exampleImages.map((example) => (
                      <button
                        key={example.id}
                        type="button"
                        className="inline-flex items-center justify-center rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition-colors hover:border-blue-500 hover:bg-blue-100 hover:text-blue-700"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          handleExampleSelect(example)
                        }}
                        data-testid={`example-btn-${example.id}`}
                      >
                        {example.label}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="mt-4" style={{ marginTop: '1rem' }}>
                <button
                  type="button"
                  className={`btn-primary ${chooseButtonClassName} ${isPreparing ? 'opacity-60 cursor-not-allowed' : ''}`}
                  style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', backgroundColor: '#2563eb', color: '#fff', ...chooseButtonStyle }}
                  data-testid="choose-file-btn"
                  aria-describedby="upload-instructions upload-supports"
                  onClick={handleChooseFileClick}
                  disabled={isPreparing}
                >
                  {resolvedChooseFileLabel}
                </button>
              </div>
            </div>
          )}
          {error && (
            <p className="mt-3 text-sm text-red-600" role="alert" aria-live="polite">{error}</p>
          )}
          <p ref={uploadLiveRef} className="sr-only" aria-live="polite" />
          {isPreparing && (
            <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/80" role="status" aria-live="polite" data-testid="upload-preparing">
              <div className="flex items-center gap-3 text-sm font-medium text-gray-600">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" aria-hidden="true" />
                <span>{t('tool.preparing')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ToolSection;
